/**
 * PRIMITIVAS CRIPTOGRÁFICAS OFFLINE (FASE 5.6)
 *
 * Único módulo que toca `crypto.subtle`. Todo lo demás (perfil.ts, db.ts)
 * pasa por aquí — nunca reimplementa AES-GCM/PBKDF2 a mano en otro sitio
 * (encargo §20: "no inventar variantes por store").
 *
 * JERARQUÍA DE CLAVES (§4)
 *
 *   PIN --PBKDF2-SHA-256--> KEK --AES-GCM wrap--> DEK (envuelta, persistida)
 *                                                   |
 *                                    AES-GCM (registros/blobs), en memoria
 *
 * La DEK NUNCA se genera pensada para vivir como bytes planos en una
 * variable JS más tiempo del imprescindible. Se usa `wrapKey`/`unwrapKey`
 * de Web Crypto en vez de "exportKey + cifrar a mano": son las únicas
 * operaciones de la API que cifran/descifrán una CLAVE sin que sus bytes
 * en claro lleguen nunca a ser un valor JS inspeccionable — ni siquiera
 * transitoriamente. La única excepción es `crearEnvolturaDek`, donde la
 * DEK se genera `extractable: true` porque `wrapKey` lo exige para poder
 * envolverla la primera vez (y en `cambiarPin`, por la misma razón) — en
 * cuanto existe la envoltura, el resto de este módulo siempre reimporta
 * una copia `extractable: false` para el uso normal (desbloqueo, cifrado
 * de registros): ver comentario de `desenvolverDek`.
 *
 * AAD (§6)
 *
 * Todo cifrado de registro/blob liga el ciphertext a
 * `neatos:v<cryptoVersion>:<perfilId>:<store>:<claveLogica>` como
 * Additional Authenticated Data. Copiar un ciphertext válido de una fila a
 * otra clave, store o perfil hace que la autenticación de AES-GCM falle
 * SIEMPRE — es la propiedad que impide "ciphertext de perfil A en perfil
 * B" (§35, test F) sin tener que comparar nada a mano.
 *
 * ERRORES (§11/§34)
 *
 * `ErrorDescifrado` es el ÚNICO tipo de error que puede salir de
 * `desenvolverDek`/`descifrarRegistro`/`descifrarBlob` — PIN incorrecto,
 * ciphertext corrupto, IV alterado, AAD que no corresponde y "ciphertext
 * de otro perfil/store/registro" son, deliberadamente, la MISMA forma de
 * fallo hacia quien llama: ni este módulo ni la UI deben poder distinguir
 * "PIN malo" de "alguien manipuló un byte" — eso ya sería revelar
 * información de más (§11: "error genérico, no revelar información
 * adicional").
 */

// ------------------------------------------------------------- constantes

/** Versión del formato criptográfico — viaja en cada envoltura/envelope y en cada AAD. Cambiarla es la única palanca para migrar de parámetros en el futuro (nunca reinterpretar filas antiguas con parámetros nuevos). */
export const CRYPTO_VERSION = 1;

const NAMESPACE_AAD = 'neatos';

/**
 * PBKDF2-SHA-256, 600 000 iteraciones — banda baja de la recomendación
 * OWASP vigente para PBKDF2-HMAC-SHA256 en 2026. `crypto.subtle` lo
 * ejecuta en código nativo del navegador, no en JS: del orden de un par de
 * cientos de milisegundos incluso en un móvil de gama media, nunca
 * segundos. Centralizado aquí (§5: "no repartir números mágicos") y
 * versionado junto a `CRYPTO_VERSION` — subirlo en el futuro no invalida
 * perfiles ya creados porque cada envoltura guarda sus propias
 * `iteraciones`, nunca esta constante.
 */
const KDF_ITERACIONES_V1 = 600_000;
const KDF_HASH = 'SHA-256';
const KDF_ALGORITMO = 'PBKDF2';

/** 128 bits — banda alta habitual para un salt de KDF, nunca reutilizado entre perfiles (aleatorio por perfil). */
const LONGITUD_SALT_BYTES = 16;
/** 96 bits — el tamaño de IV recomendado para AES-GCM; nunca reutilizado con la misma clave (aleatorio por operación, §5). */
const LONGITUD_IV_BYTES = 12;
const LONGITUD_DEK_BITS = 256;

// ----------------------------------------------------------------- tipos

export interface ParametrosKdf {
  readonly algoritmo: 'PBKDF2';
  readonly hash: 'SHA-256';
  readonly iteraciones: number;
  readonly salt: Uint8Array;
}

/** Lo que se PERSISTE en `PerfilLocal` (perfil.ts) — nunca la DEK en claro. */
export interface EnvolturaDek {
  readonly cryptoVersion: number;
  readonly kdf: ParametrosKdf;
  readonly ivDek: Uint8Array;
  readonly dekEnvuelta: Uint8Array;
}

/** Formato único de todo dato cifrado en reposo (§20) — nunca variantes por store. */
export interface EnvelopeCifrado {
  readonly cryptoVersion: number;
  readonly iv: Uint8Array;
  readonly ciphertext: Uint8Array;
}

/**
 * Único tipo de error que sale de una operación de descifrado —
 * deliberadamente sin más detalle que un mensaje genérico. Ver cabecera
 * del fichero.
 */
export class ErrorDescifrado extends Error {
  constructor() {
    super('No se han podido abrir los datos cifrados.');
    this.name = 'ErrorDescifrado';
  }
}

// -------------------------------------------------------------------- aad

/**
 * `store`/`claveLogica` identifican el registro dentro del perfil —
 * `claveLogica` es siempre el valor de la clave primaria de la fila
 * (`id`, `idOperacionCliente`, `hash`...), nunca un valor inventado aparte.
 */
function construirAad(perfilId: string, store: string, claveLogica: string): Uint8Array {
  return new TextEncoder().encode(`${NAMESPACE_AAD}:v${String(CRYPTO_VERSION)}:${perfilId}:${store}:${claveLogica}`);
}

// -------------------------------------------------------------------- kdf

/**
 * PIN -> KEK. La KEK sólo se pide con los usos `wrapKey`/`unwrapKey`
 * (nunca `encrypt`/`decrypt`): estructuralmente no puede usarse para
 * cifrar un registro aunque alguien lo intentara por error de programación
 * — su único propósito posible es envolver/desenvolver una DEK.
 */
export async function derivarKek(pin: string, salt: Uint8Array, iteraciones: number): Promise<CryptoKey> {
  const claveBase = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), KDF_ALGORITMO, false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: KDF_ALGORITMO, salt: toBufferSource(salt), iterations: iteraciones, hash: KDF_HASH },
    claveBase,
    { name: 'AES-GCM', length: LONGITUD_DEK_BITS },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

// --------------------------------------------------------- ciclo de la dek

/**
 * Primera creación de un perfil (§4/§7): DEK aleatoria + KEK derivada del
 * PIN + envoltura. Devuelve TAMBIÉN una copia de la DEK ya lista para usar
 * en esta misma sesión (no extractable) — evita desenvolver dos veces
 * seguidas justo después de crear el perfil.
 */
export async function crearEnvolturaDek(pin: string): Promise<{ envoltura: EnvolturaDek; dek: CryptoKey }> {
  const salt = crypto.getRandomValues(new Uint8Array(LONGITUD_SALT_BYTES));
  const iteraciones = KDF_ITERACIONES_V1;
  const kek = await derivarKek(pin, salt, iteraciones);

  //  `extractable: true` sólo porque `wrapKey` lo exige — ver cabecera del
  //  fichero. Esta referencia se descarta en cuanto termina esta función;
  //  la DEK que de verdad queda viva en memoria (`dek`, más abajo) es una
  //  reimportación no extractable de la MISMA clave.
  const dekExtraible = await crypto.subtle.generateKey({ name: 'AES-GCM', length: LONGITUD_DEK_BITS }, true, [
    'encrypt',
    'decrypt',
  ]);
  const ivDek = crypto.getRandomValues(new Uint8Array(LONGITUD_IV_BYTES));
  const dekEnvuelta = new Uint8Array(
    await crypto.subtle.wrapKey('raw', dekExtraible, kek, { name: 'AES-GCM', iv: toBufferSource(ivDek) }),
  );

  const envoltura: EnvolturaDek = {
    cryptoVersion: CRYPTO_VERSION,
    kdf: { algoritmo: KDF_ALGORITMO, hash: KDF_HASH, iteraciones, salt },
    ivDek,
    dekEnvuelta,
  };

  const dek = await desenvolverDek(pin, envoltura);
  return { envoltura, dek };
}

/**
 * Desbloqueo (§11): deriva la KEK del PIN introducido y desenvuelve la DEK
 * como CryptoKey NO extractable (§4/§14 — "importar como CryptoKey no
 * extractable cuando sea posible"; a diferencia de `crearEnvolturaDek`,
 * aquí no hace falta volver a envolverla, así que no hay motivo para que
 * sea extraíble). Un PIN incorrecto hace fallar la autenticación AES-GCM
 * del propio `unwrapKey` — `ErrorDescifrado` es la única señal, nunca un
 * mensaje distinto para "PIN malo" frente a "envoltura corrupta".
 */
export async function desenvolverDek(pin: string, envoltura: EnvolturaDek): Promise<CryptoKey> {
  try {
    const kek = await derivarKek(pin, envoltura.kdf.salt, envoltura.kdf.iteraciones);
    return await crypto.subtle.unwrapKey(
      'raw',
      toBufferSource(envoltura.dekEnvuelta),
      kek,
      { name: 'AES-GCM', iv: toBufferSource(envoltura.ivDek) },
      { name: 'AES-GCM', length: LONGITUD_DEK_BITS },
      false,
      ['encrypt', 'decrypt'],
    );
  } catch {
    throw new ErrorDescifrado();
  }
}

/**
 * Cambio de PIN (§12): NUNCA recifra el almacén — sólo produce una
 * envoltura nueva para la MISMA DEK. Desenvuelve la DEK actual como
 * extractable (sólo aquí hace falta, para poder volver a envolverla) y la
 * envuelve bajo una KEK nueva con salt/IV nuevos. La DEK activa en memoria
 * de la sesión en curso (no extractable) no se toca: sigue sirviendo
 * exactamente igual, esto sólo cambia CÓMO se recupera la próxima vez.
 */
export async function cambiarPin(
  pinActual: string,
  envolturaActual: EnvolturaDek,
  pinNuevo: string,
): Promise<EnvolturaDek> {
  let dekExtraible: CryptoKey;
  try {
    const kekActual = await derivarKek(pinActual, envolturaActual.kdf.salt, envolturaActual.kdf.iteraciones);
    dekExtraible = await crypto.subtle.unwrapKey(
      'raw',
      toBufferSource(envolturaActual.dekEnvuelta),
      kekActual,
      { name: 'AES-GCM', iv: toBufferSource(envolturaActual.ivDek) },
      { name: 'AES-GCM', length: LONGITUD_DEK_BITS },
      true,
      ['encrypt', 'decrypt'],
    );
  } catch {
    throw new ErrorDescifrado();
  }

  const salt = crypto.getRandomValues(new Uint8Array(LONGITUD_SALT_BYTES));
  const iteraciones = KDF_ITERACIONES_V1;
  const kekNueva = await derivarKek(pinNuevo, salt, iteraciones);
  const ivDek = crypto.getRandomValues(new Uint8Array(LONGITUD_IV_BYTES));
  const dekEnvuelta = new Uint8Array(
    await crypto.subtle.wrapKey('raw', dekExtraible, kekNueva, { name: 'AES-GCM', iv: toBufferSource(ivDek) }),
  );

  return {
    cryptoVersion: CRYPTO_VERSION,
    kdf: { algoritmo: KDF_ALGORITMO, hash: KDF_HASH, iteraciones, salt },
    ivDek,
    dekEnvuelta,
  };
}

// --------------------------------------------------------- registros/blobs

async function cifrarBytes(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  datos: Uint8Array,
): Promise<EnvelopeCifrado> {
  const iv = crypto.getRandomValues(new Uint8Array(LONGITUD_IV_BYTES));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: toBufferSource(iv), additionalData: toBufferSource(construirAad(perfilId, store, claveLogica)) },
      dek,
      toBufferSource(datos),
    ),
  );
  return { cryptoVersion: CRYPTO_VERSION, iv, ciphertext };
}

async function descifrarBytes(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  envelope: EnvelopeCifrado,
): Promise<Uint8Array> {
  if (envelope.cryptoVersion !== CRYPTO_VERSION) throw new ErrorDescifrado();
  try {
    const plano = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: toBufferSource(envelope.iv),
        additionalData: toBufferSource(construirAad(perfilId, store, claveLogica)),
      },
      dek,
      toBufferSource(envelope.ciphertext),
    );
    return new Uint8Array(plano);
  } catch {
    throw new ErrorDescifrado();
  }
}

/** Cifra un registro (cualquier valor serializable a JSON) — el uso normal desde `db.ts`. */
export async function cifrarRegistro<T>(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  valor: T,
): Promise<EnvelopeCifrado> {
  const bytes = new TextEncoder().encode(JSON.stringify(valor));
  return cifrarBytes(dek, perfilId, store, claveLogica, bytes);
}

/** Descifra un registro cifrado con `cifrarRegistro`. `ErrorDescifrado` en cualquier fallo — PIN/DEK equivocada, ciphertext ajeno, corrupción. */
export async function descifrarRegistro<T>(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  envelope: EnvelopeCifrado,
): Promise<T> {
  const bytes = await descifrarBytes(dek, perfilId, store, claveLogica, envelope);
  try {
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    //  JSON corrupto tras una autenticación AES-GCM correcta no debería
    //  poder pasar nunca — pero si pasara, se trata exactamente igual que
    //  cualquier otro fallo de descifrado (§34: nunca continuar con datos
    //  parciales).
    throw new ErrorDescifrado();
  }
}

/**
 * Cifra el contenido de un `Blob` (documento/evidencia). El SHA-256 de
 * negocio (§22) se calcula SIEMPRE sobre el `Blob` en claro, ANTES de
 * llamar aquí — este módulo no sabe nada de ese hash y nunca lo recalcula.
 */
export async function cifrarBlob(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  blob: Blob,
): Promise<EnvelopeCifrado> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return cifrarBytes(dek, perfilId, store, claveLogica, bytes);
}

export async function descifrarBlob(
  dek: CryptoKey,
  perfilId: string,
  store: string,
  claveLogica: string,
  envelope: EnvelopeCifrado,
  mime: string,
): Promise<Blob> {
  const bytes = await descifrarBytes(dek, perfilId, store, claveLogica, envelope);
  return new Blob([toBufferSource(bytes)], { type: mime });
}

// -------------------------------------------------------------- utilidad

/**
 * `fake-indexeddb`/algunos navegadores devuelven vistas `Uint8Array` sobre
 * un `ArrayBuffer` que puede no empezar en el byte 0 tras un roundtrip de
 * `structuredClone` — Web Crypto exige un `ArrayBuffer`/vista "limpia".
 * Copiar siempre es barato (claves y registros son pequeños) y evita una
 * clase entera de fallos intermitentes difíciles de reproducir; de paso
 * garantiza el tipo `Uint8Array<ArrayBuffer>` que exige `BufferSource` —
 * una vista sobre un `Uint8Array<ArrayBufferLike>` genérico (p.ej. el que
 * devuelve `.slice()`) no basta para TypeScript aunque en tiempo de
 * ejecución sea idéntica.
 */
function toBufferSource(datos: Uint8Array): Uint8Array<ArrayBuffer> {
  const copia = new Uint8Array(datos.byteLength);
  copia.set(datos);
  return copia;
}
